import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Navigation from "../components/Navigation";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  ArrowLeft,
  Image,
  Video,
  FileText,
  Send,
  Sparkles,
  Upload,
  Link2,
  X,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

export default function CreatePost() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [content, setContent] = useState("");
  const [mediaType, setMediaType] = useState("text");
  const [mediaUrl, setMediaUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadMethod, setUploadMethod] = useState("upload"); // 'upload' or 'url'

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const isVideo = file.type.startsWith("video/");
    const isImage = file.type.startsWith("image/");
    
    if (!isVideo && !isImage) {
      toast.error("Please select an image or video file");
      return;
    }

    // Validate file size
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large. Max ${isVideo ? "50MB" : "10MB"} allowed`);
      return;
    }

    setUploadingMedia(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/upload/media`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setMediaUrl(response.data.url);
      setMediaType(response.data.media_type);
      setUploadedFile({
        name: file.name,
        type: response.data.media_type,
        preview: URL.createObjectURL(file),
      });
      toast.success("Media uploaded successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload media");
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleRemoveMedia = () => {
    setMediaUrl("");
    setMediaType("text");
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUrlChange = (url) => {
    setMediaUrl(url);
    // Auto-detect media type from URL
    if (url.match(/\.(mp4|webm|ogg|mov)(\?|$)/i)) {
      setMediaType("video");
    } else if (url.match(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i)) {
      setMediaType("photo");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) {
      toast.error("Please write something positive to share!");
      return;
    }

    setSubmitting(true);
    try {
      // Construct full URL for uploaded files
      let fullMediaUrl = mediaUrl;
      if (mediaUrl && mediaUrl.startsWith("/api/uploads/")) {
        fullMediaUrl = `${process.env.REACT_APP_BACKEND_URL}${mediaUrl}`;
      }

      await axios.post(`${API}/posts`, {
        content,
        media_type: mediaType,
        media_url: fullMediaUrl || null,
      });
      toast.success("Post shared! Thanks for spreading positivity! 🌟");
      navigate("/feed");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create post";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Get preview URL for display
  const getPreviewUrl = () => {
    if (uploadedFile?.preview) return uploadedFile.preview;
    if (mediaUrl) {
      if (mediaUrl.startsWith("/api/uploads/")) {
        return `${process.env.REACT_APP_BACKEND_URL}${mediaUrl}`;
      }
      return mediaUrl;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate("/feed")}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>
            <h1 className="text-xl font-bold text-foreground">Create Post</h1>
            <div className="w-20"></div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info card */}
        <div className="card-happy bg-gradient-to-r from-primary/5 to-secondary/5 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Sparkles className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground mb-1">
                Share something positive!
              </h2>
              <p className="text-muted-foreground text-sm">
                Your posts are automatically checked to ensure they spread
                happiness. Upload photos or videos from your gallery, or share via URL!
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="card-happy space-y-6">
            {/* Content */}
            <div className="space-y-2">
              <Label htmlFor="content">What's on your mind?</Label>
              <Textarea
                id="content"
                data-testid="post-content-input"
                placeholder="Share your story, success, or something that made you smile today..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="input-happy min-h-[150px] text-lg resize-none"
              />
              <p className="text-right text-sm text-muted-foreground">
                {content.length} characters
              </p>
            </div>

            {/* Media upload section */}
            <div className="space-y-4">
              <Label>Add Media (Optional)</Label>
              
              <Tabs value={uploadMethod} onValueChange={setUploadMethod} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="upload" className="flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    Upload File
                  </TabsTrigger>
                  <TabsTrigger value="url" className="flex items-center gap-2">
                    <Link2 className="w-4 h-4" />
                    Paste URL
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="upload" className="space-y-4">
                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*,video/*"
                    className="hidden"
                    data-testid="media-file-input"
                  />

                  {/* Upload area */}
                  {!uploadedFile ? (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingMedia}
                      data-testid="upload-media-btn"
                      className="w-full border-2 border-dashed border-stone-200 rounded-2xl p-8 hover:border-primary/50 hover:bg-primary/5 transition-all group"
                    >
                      {uploadingMedia ? (
                        <div className="flex flex-col items-center gap-3">
                          <Loader2 className="w-10 h-10 text-primary animate-spin" />
                          <span className="text-muted-foreground">Uploading...</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                            <Upload className="w-8 h-8 text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>
                          <div className="text-center">
                            <p className="font-medium text-foreground">
                              Click to upload from gallery
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                              Photos (max 10MB) or Videos (max 50MB)
                            </p>
                          </div>
                        </div>
                      )}
                    </button>
                  ) : (
                    <div className="relative">
                      <div className="rounded-2xl overflow-hidden bg-muted">
                        {uploadedFile.type === "photo" ? (
                          <img
                            src={uploadedFile.preview}
                            alt="Upload preview"
                            className="w-full h-64 object-cover"
                          />
                        ) : (
                          <video
                            src={uploadedFile.preview}
                            className="w-full h-64 object-cover"
                            controls
                          />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveMedia}
                        data-testid="remove-media-btn"
                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                      <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
                        {uploadedFile.type === "photo" ? (
                          <Image className="w-4 h-4" />
                        ) : (
                          <Video className="w-4 h-4" />
                        )}
                        <span>{uploadedFile.name}</span>
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="url" className="space-y-4">
                  <div className="space-y-2">
                    <input
                      type="url"
                      data-testid="media-url-input"
                      placeholder="https://example.com/image.jpg or video.mp4"
                      value={uploadMethod === "url" ? mediaUrl : ""}
                      onChange={(e) => handleUrlChange(e.target.value)}
                      className="w-full input-happy"
                    />
                    <p className="text-sm text-muted-foreground">
                      Paste a direct link to an image or video
                    </p>
                  </div>

                  {/* URL Preview */}
                  {uploadMethod === "url" && mediaUrl && (
                    <div className="relative">
                      <div className="rounded-2xl overflow-hidden bg-muted">
                        {mediaType === "photo" ? (
                          <img
                            src={mediaUrl}
                            alt="URL preview"
                            className="w-full h-48 object-cover"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : mediaType === "video" ? (
                          <video
                            src={mediaUrl}
                            className="w-full h-48 object-cover"
                            controls
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : null}
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>

            {/* Media type indicator */}
            {(uploadedFile || (uploadMethod === "url" && mediaUrl)) && (
              <div className="flex items-center gap-2 px-4 py-2 bg-muted/50 rounded-xl">
                {mediaType === "photo" ? (
                  <Image className="w-5 h-5 text-primary" />
                ) : mediaType === "video" ? (
                  <Video className="w-5 h-5 text-secondary" />
                ) : (
                  <FileText className="w-5 h-5 text-muted-foreground" />
                )}
                <span className="text-sm font-medium capitalize">
                  {mediaType} post
                </span>
              </div>
            )}
          </div>

          {/* Submit */}
          <Button
            type="submit"
            data-testid="submit-post-btn"
            disabled={submitting || !content.trim()}
            className="w-full btn-primary py-6 text-lg"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Checking positivity...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Share Post
              </>
            )}
          </Button>
        </form>
      </main>

      <Navigation />
    </div>
  );
}
